import type { TrackingEvent } from '../../../../../backend/src/shared/action-registry';
import type { ZipOption } from '../../../../../backend/src/types/types';
import { DocumentDownloadOptions, DownloadFormat, Fonts, TextAlignment } from '../../../../../backend/src/types/types';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';

export type { ZipOption };

export type DownloadKind = 'written' | 'table' | 'list' | 'zip' | 'image' | 'bundle';
export type ImageFormat = 'png' | 'svg';
export type AnyFormat = DownloadFormat | ImageFormat | 'zip' | 'pdf';

export interface ColumnsSpec {
  available: string[];
  default: string[];
  relativity?: FilterField[];
}

export interface DownloadFormValues<TFormat extends AnyFormat = AnyFormat> {
  filename: string;
  format: TFormat;
  scope?: string;
  columns?: string[];
  relativityColumns?: string[];
  style?: string;
  docOptions?: DocumentDownloadOptions;
  includeSourceDocuments?: boolean;
  orderByDate?: boolean;
  zipContent?: ZipOption;
}

interface BaseVariant<TFormat extends AnyFormat = AnyFormat> {
  id: string;
  kind: DownloadKind;
  label: string;
  description?: string;
  icon?: string;
  formats: TFormat[];
  defaultFilename?: string;
  columns?: ColumnsSpec;
  style?: boolean;
  /** Noun used in the style field copy (e.g. "fact", "document"). Required when `style` is true. */
  styleItemNoun?: string;
  docOptions?: boolean;
  sourceDocs?: boolean;
  /** Show originals/processed PDF picker (for document zip variants). */
  showZipContent?: boolean;
  largeExportWarning?: boolean;
}

/**
 * Post-creation contract shared by any variant that runs as a tracked async export
 * (standard async variants and bundles).
 */
export interface ExportProgress<TDownloadArg> {
  /** SSE endpoint base URL (exportId appended as ?exportId= by the progress modal). */
  progressEndpoint(exportId: string): string;
  /**
   * Optional REST endpoint URL builder (exportId appended as `?exportId=`)
   * returning the persisted terminal status. The progress modal polls it when
   * the SSE channel drops. Sibling to `progressEndpoint` and gated by the same
   * per-resource download permission.
   */
  statusEndpoint?(exportId: string): string;
  /** Called when the user clicks "Download" on the ready progress modal. */
  downloadReady(exportId: string, arg: TDownloadArg | undefined, filename?: string): Promise<void>;
  readyDescription?: string;
  downloadButtonText?: string;
}

export type AsyncVariant<TFormat extends AnyFormat = AnyFormat> = BaseVariant<TFormat> &
  ExportProgress<TFormat> & {
    mode: 'async';
    mutate(values: DownloadFormValues<TFormat>): Promise<{ exportId: string; jobId?: string }>;
  };

export type SyncVariant<TFormat extends AnyFormat = AnyFormat> = BaseVariant<TFormat> & {
  mode: 'sync';
  mutate(values: DownloadFormValues<TFormat>): Promise<void>;
};

export interface BundleItemSeed {
  id: string;
  schemaName: 'doc' | 'fact' | 'inconsistency' | 'entity';
  schemaId: string;
  originalName: string;
  pageCount?: number;
  dateText?: string | null;
}

export interface BundleVariantConfig {
  matterId: string;
  subsetId: string;
  itemType: 'doc' | 'fact' | 'inconsistency' | 'entity';
  useItems: () => { items: BundleItemSeed[]; isLoading: boolean; isError?: boolean };
  defaultName: string;
}

export interface BundleVariant extends ExportProgress<string> {
  id: string;
  kind: 'bundle';
  label: string;
  description?: string;
  icon?: string;
  bundle: BundleVariantConfig;
  /** When set, the variant tile is rendered disabled and the reason is shown as a tooltip. */
  disabledReason?: string;
}

export type DownloadVariant<TFormat extends AnyFormat = AnyFormat> =
  | AsyncVariant<TFormat>
  | SyncVariant<TFormat>
  | BundleVariant;

export const isBundleVariant = (v: DownloadVariant): v is BundleVariant => v.kind === 'bundle';

export interface DownloadScope {
  id: string;
  label: string;
  totalCount?: number;
}

/**
 * Shape emitted to Mixpanel for any download tracking fire. The page supplies
 * IDs via `DownloadTracking.basePayload`; the modal supplies `variant` and the
 * other context fields at fire time.
 */
export interface DownloadTrackingPayload {
  matterId: string;
  subsetId: string;
  variant: DownloadKind;
  docChronologyId?: string;
  isAllDocuments?: boolean;
  downloadFormat?: AnyFormat;
  scope?: string;
  withSourceDocuments?: boolean;
  zipContent?: ZipOption;
  itemCount?: number;
}

export interface DownloadTracking {
  /** Mixpanel event name fired on confirm. Receives a `DownloadTrackingPayload`. */
  event: TrackingEvent;
  /** Static payload from the page. The modal supplies `variant` at fire time. */
  basePayload: Omit<DownloadTrackingPayload, 'variant'>;
}

export interface DownloadDefinition {
  title?: string;
  scopes?: DownloadScope[];
  defaultScopeId?: string;
  variants: DownloadVariant[];
  defaultVariantId?: string;
  totalCount?: number;
  tracking?: DownloadTracking;
}

export const defaultDocOptions = (): DocumentDownloadOptions => ({
  fontFamily: Fonts.TimesNewRoman,
  textAlignment: TextAlignment.Left,
  includeReferences: true,
});

export const kindShowsFilename = (kind: DownloadKind): boolean => kind !== 'image' && kind !== 'bundle';

export const defaultIconForKind = (kind: DownloadKind): string => {
  switch (kind) {
    case 'table':
      return 'lucide--table';
    case 'written':
      return 'lucide--file-text';
    case 'list':
      return 'lucide--list';
    case 'zip':
      return 'lucide--folder-archive';
    case 'image':
      return 'lucide--image';
    case 'bundle':
      return 'lucide--package';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};
